import React, { useState, useEffect } from 'react';
import { Server, Activity, AlertTriangle, CheckCircle, XCircle, Filter, Download, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL !== undefined && process.env.REACT_APP_BACKEND_URL !== '' ? process.env.REACT_APP_BACKEND_URL : '';

const SystemLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterKategori, setFilterKategori] = useState('');
  const [filterAksi, setFilterAksi] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filterKategori, filterAksi, filterStatus]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (filterKategori) queryParams.append('kategori', filterKategori);
      if (filterAksi) queryParams.append('aksi', filterAksi);

      const response = await fetch(`${API_URL}/api/system-logs?${queryParams}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Gagal mengambil logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/system-logs/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Gagal mengambil stats:', err);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'success') {
      return <CheckCircle size={16} className="text-green-600" />;
    } else if (status === 'failed') {
      return <XCircle size={16} className="text-red-600" />;
    } else {
      return <AlertTriangle size={16} className="text-yellow-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'bg-green-100 text-green-800 border-green-300',
      failed: 'bg-red-100 text-red-800 border-red-300',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold border ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getKategoriBadge = (kategori) => {
    const colors = {
      auth: 'bg-purple-100 text-purple-800',
      transaksi: 'bg-blue-100 text-blue-800',
      laporan: 'bg-green-100 text-green-800',
      user: 'bg-orange-100 text-orange-800',
      sistem: 'bg-gray-100 text-gray-800',
      it_edit: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[kategori] || 'bg-gray-100 text-gray-800'}`}>
        {kategori}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Server size={28} className="text-primary-600" />
            System Logs
          </h2>
          <p className="text-gray-600">Monitoring dan audit trail aktivitas sistem</p>
        </div>
        <button
          onClick={() => {
            const token = localStorage.getItem('token');
            fetch(`${API_URL}/api/system-logs/export`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => {
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `system-logs-${new Date().toISOString().split('T')[0]}.json`;
              a.click();
            });
          }}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 font-semibold"
        >
          <Download size={20} />
          Export Logs
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Logs</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_logs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Error (24h)</div>
            <div className="text-2xl font-bold text-red-600">{stats.error_count}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Recent 24h</div>
            <div className="text-2xl font-bold text-blue-600">{stats.recent_24h}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Success</div>
            <div className="text-2xl font-bold text-green-600">{stats.status_stats?.success || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Failed</div>
            <div className="text-2xl font-bold text-red-600">{stats.status_stats?.failed || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-500" />
            <span className="font-semibold text-gray-700">Filter:</span>
          </div>

          <select
            value={filterKategori}
            onChange={(e) => setFilterKategori(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Semua Kategori</option>
            <option value="auth">Auth</option>
            <option value="transaksi">Transaksi</option>
            <option value="laporan">Laporan</option>
            <option value="user">User</option>
            <option value="sistem">Sistem</option>
            <option value="it_edit">IT Edit</option>
          </select>

          <select
            value={filterAksi}
            onChange={(e) => setFilterAksi(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Semua Aksi</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="view">View</option>
            <option value="error">Error</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Semua Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="warning">Warning</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Kategori</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Aksi</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Detail</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <p className="mt-2 text-gray-600">Memuat logs...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <Activity size={48} className="mx-auto mb-4 text-gray-400" />
                    <p>Tidak ada logs yang ditemukan</p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(log.timestamp).toLocaleString('id-ID')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.role}</div>
                      <div className="text-xs text-gray-500">{log.user_id?.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getKategoriBadge(log.kategori)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.aksi}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {JSON.stringify(log.detail)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        {getStatusBadge(log.status)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Info:</strong> System logs mencatat semua aktivitas dalam sistem termasuk login, transaksi,
            edit/delete data, dan error. Logs ini berguna untuk audit trail dan troubleshooting.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemLogViewer;
